const FORMAT_PATTERN = /^a11ypilot_[a-z0-9_]+$/;

export class A11yPilotApiError extends Error {
  constructor(message, { status = 0, code = 'api-error', details = null } = {}) {
    super(message);
    this.name = 'A11yPilotApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (_error) {
    throw new A11yPilotApiError(
      response.ok ? 'Il servizio ha restituito una risposta non valida.' : 'Il servizio non è al momento disponibile.',
      { status: response.status, code: 'invalid-json-response' },
    );
  }
}

function apiErrorFromResponse(response, body) {
  const payload = isPlainObject(body?.error) ? body.error : {};
  const defaultMessage = response.status === 403
    ? 'Non hai accesso al workspace richiesto.'
    : response.status === 401
      ? 'La sessione non è più valida. Accedi di nuovo.'
      : 'La richiesta non è riuscita. Riprova.';

  return new A11yPilotApiError(String(payload.message || defaultMessage), {
    status: response.status,
    code: String(payload.code || `http-${response.status}`),
    details: payload.details ?? null,
  });
}

function isAppCheckFailure(body) {
  const error = isPlainObject(body?.error) ? body.error : {};
  const signal = `${String(error.code || '')} ${String(error.message || '')}`;
  return /app[-_ ]?check/i.test(signal);
}

/**
 * Authenticated adapter for the existing a11ypilot_* contract.
 *
 * The tenant identifier is deployment configuration, but it is only a request
 * hint: the API remains authoritative and verifies membership for every call.
 */
export function createA11yPilotApi({
  auth,
  tenantId,
  endpoint = '/api',
  getAppCheckToken = async () => '',
  fetchImpl = globalThis.fetch,
} = {}) {
  if (!auth || typeof auth !== 'object') throw new TypeError('auth è obbligatorio.');
  if (!tenantId) throw new TypeError('tenantId è obbligatorio.');
  if (typeof fetchImpl !== 'function') throw new TypeError('fetch non è disponibile.');

  async function request(format, payload = {}, forceRefresh = false) {
    if (!FORMAT_PATTERN.test(String(format || ''))) {
      throw new TypeError('Formato API A11yPilot non valido.');
    }
    if (!isPlainObject(payload)) throw new TypeError('Il payload API deve essere un oggetto.');

    const user = auth.currentUser;
    if (!user || typeof user.getIdToken !== 'function') {
      throw new A11yPilotApiError('Devi accedere per usare A11yPilot.', {
        status: 401,
        code: 'unauthenticated',
      });
    }

    let token;
    let appCheckToken;
    try {
      [token, appCheckToken] = await Promise.all([
        user.getIdToken(forceRefresh),
        getAppCheckToken(forceRefresh),
      ]);
    } catch (error) {
      throw new A11yPilotApiError('Impossibile verificare la sessione. Riprova.', {
        status: 401,
        code: 'token-unavailable',
        details: error instanceof Error ? error.message : null,
      });
    }
    const headers = {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (appCheckToken) headers['X-Firebase-AppCheck'] = appCheckToken;
    let response;
    try {
      response = await fetchImpl(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({ format, tenantId, payload }),
      });
    } catch (error) {
      throw new A11yPilotApiError('Connessione al servizio non riuscita.', {
        code: 'network-error',
        details: error instanceof Error ? error.message : null,
      });
    }

    const body = await parseResponse(response);
    if (!response.ok) {
      const refreshableAppCheckError = response.status === 403 && isAppCheckFailure(body);
      if (!forceRefresh && (response.status === 401 || refreshableAppCheckError)) {
        return request(format, payload, true);
      }
      throw apiErrorFromResponse(response, body);
    }
    if (!isPlainObject(body)) {
      throw new A11yPilotApiError('Il servizio ha restituito una risposta non valida.', {
        status: response.status,
        code: 'invalid-response-shape',
      });
    }
    return body;
  }

  return Object.freeze({ postReport: request });
}

/** Supplies the successful access probe to the view exactly once. */
export function withPrimedOverview(postReport, overview) {
  let primed = overview;
  return async (format, payload = {}) => {
    if (format === 'a11ypilot_overview_get' && primed) {
      const response = primed;
      primed = null;
      return response;
    }
    return postReport(format, payload);
  };
}
